<template>
  <div
    class="ex-org-position-card"
    :class="{ inactive: !isActiveState, hovered: hovered, 'ex-org-position-card--redesign': redesignEnabled }"
    @click="handleCardClick"
  >
    <template v-if="redesignEnabled">
      <div class="position-header-row">
        <span
          v-if="showCapacityTrigger"
          ref="capacityWrap"
          class="position-capacity-trigger-wrap"
          @click.stop
        >
          <PositionCapacityTrigger
            :capacity="triggerCapacity"
            :allocation="triggerAllocation"
            :open="capacityOpen"
            :disabled="!isActiveState"
            @click="onClickCapacityTrigger"
          />
          <v-menu
            v-if="capacityEditable"
            v-model="capacityOpen"
            :activator="capacityWrap"
            :open-on-click="false"
            :close-on-content-click="false"
            :top="capacityOpensUp"
            offset-y
            transition="fade-transition"
            content-class="position-card__capacity-menu"
          >
            <CapacitySelector
              :value="triggerCapacity"
              :active="capacityOpen"
              :flipped="capacityOpensUp"
              @input="onSelectCapacity"
            />
          </v-menu>
        </span>
        <div class="new-badge" v-if="isNew">New</div>
        <div class="position-info">
          <span class="position-code">{{ displayPositionCode }}</span>
          <span class="position-name">{{ position_name }}</span>
        </div>
      </div>

      <PositionValidityBadge
        class="position-counters"
        v-bind="validityBadgeProps"
        :show-capacity-icon="!showCapacityTrigger"
        @click.stop="onClickValidityBadge"
      />

      <PositionDepartment v-if="showDepartmentLine" :department="department" />
    </template>

    <template v-else>
    <div class="badges-section">
      <PositionValidityBadge
        :show-role-responsibility-counts="showRoleResponsibilityCounts"
        :isInvalid="position_capacity.isInvalid"
        :directResponsibilitiesCount="position_capacity.directResponsibilitiesCount"
        :notActiveDirectResponsibilitiesCount="
          position_capacity.notActiveDirectResponsibilitiesCount
        "
        :validRolesCount="position_capacity.validRolesCount"
        :invalidRolesCount="position_capacity.invalidRolesCount"
        :validResponsibilitiesCount="
          position_capacity.validResponsibilitiesCount
        "
        :status="active ? 'active' : 'draft'"
        :paint-invalid="paintInvalid"
        :invalidity-reason="invalidity_reason"
        @click.stop="onClickValidityBadge"
      />

      <div class="new-badge" v-if="isNew">New</div>
    </div>

    <div class="position-info">
      <span class="position-code">{{ displayPositionCode }}</span>
      <span class="position-name">{{ position_name }}</span>
    </div>

    <div v-if="hasDepartment" class="position-department" data-testid="position-department">
      <span class="position-department-icon" aria-hidden="true">
        <Icon name="mini_department_tag_icon" />
      </span>
      <EXTooltip v-if="isDepartmentTruncated" placement="top" :title="department" :delay="300">
        <span class="position-department-name">{{ department }}</span>
      </EXTooltip>
      <span v-else class="position-department-name">{{ department }}</span>
    </div>
    </template>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import PositionValidityBadge from "@components/PositionValidityBadge/PositionValidityBadge.vue";
import PositionCapacityTrigger from "@components/PositionCapacityTrigger/PositionCapacityTrigger.vue";
import PositionDepartment from "@components/PositionDepartment/PositionDepartment.vue";
import CapacitySelector from "@components/CapacitySelector/CapacitySelector.vue";
import { LD_FLAG_IDEA_MDP_495, LD_FLAG_IDEA_MDP_636 } from "@components/constants/launchDarklyFlagKeys";
import { isInvalidityPainted } from "@components/constants/invalidityReasons";
import { isPositionRolesAndResponsibilitiesUiEnabled } from "@components/utils/positionFormTabs";
import Icon from "@components/icons/Icon.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";

const props = defineProps({
  position_capacity: {
    type: Object,
    required: true,
  },
  active_roles: {
    type: Number,
    default: 0,
  },
  active_responsibilities: {
    type: Number,
    default: 0,
  },
  position_name: {
    type: String,
    required: true,
  },
  position_code: {
    type: String,
    required: true,
  },
  created_on: {
    type: Date,
    required: false,
  },
  active: {
    type: Boolean,
    default: true,
  },
  /** Position status (draft, active, retired, archived). With the redesign on it sets the card State: Draft and Active are Active, Retired and Archived are Not Active. "" = use `active`. */
  status: {
    type: String,
    default: "",
  },
  hovered: {
    type: Boolean,
    default: false,
  },
  /** Department name. "" = the position has none (redesign shows "No department"); null = not provided, no department line. */
  department: {
    type: String,
    default: null,
  },
  /** ER-9940: payload `is_valid`. Only an explicit false paints the red border (never recomputed here). */
  is_valid: {
    type: Boolean,
    default: true,
  },
  /** ER-9940: payload `invalidity_reason`. POSITION_HAS_INVALID_ACTIVE_ROLE adds the red invalid-role count. */
  invalidity_reason: {
    type: String,
    default: "",
  },
  /** ER-9940: archived positions are never painted red. */
  archived: {
    type: Boolean,
    default: false,
  },
  /** An enabled capacity trigger opens the Capacity Selector and emits `capacityChange`. Off: the trigger only emits `capacityTriggerClick`. */
  capacityEditable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "clickValidityBadge",
  "positionClick",
  "capacityTriggerClick",
  "capacityChange",
]);

const ALLOCATIONS = ["not", "0.5", "2x0.5", "1"];
const ACTIVE_STATE_STATUSES = ["draft", "active"];

// Approximate height of the open Capacity Selector (tail included), used to flip it above the trigger.
const CAPACITY_SELECTOR_HEIGHT = 170;
const CAPACITY_MENU_CLASS = "position-card__capacity-menu";

const redesignEnabled = ref(false);
const validityIndicatorsEnabled = ref(true);
const capacityOpen = ref(false);
const capacityOpensUp = ref(false);
const capacityWrap = ref(null);
const proxy = getCurrentInstance()?.proxy;

onMounted(async () => {
  try {
    redesignEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_636, false)) === true;
  } catch (error) {
    redesignEnabled.value = false;
  }
  try {
    // idea-MDP-495 is a kill switch: only an explicit false restores the legacy rendering.
    validityIndicatorsEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_495, true)) !== false;
  } catch (error) {
    validityIndicatorsEnabled.value = true;
  }
});

const paintInvalid = computed(
  () =>
    validityIndicatorsEnabled.value &&
    isInvalidityPainted({
      isValid: props.is_valid,
      status: props.status,
      archived: props.archived,
    })
);

const isActiveState = computed(() =>
  redesignEnabled.value && props.status
    ? ACTIVE_STATE_STATUSES.includes(props.status.toLowerCase())
    : props.active
);

const showCapacityTrigger = computed(() => [1, 0.5].includes(props.position_capacity?.capacity));
const triggerCapacity = computed(() => props.position_capacity?.capacity);
const triggerAllocation = computed(() =>
  ALLOCATIONS.includes(props.position_capacity?.allocation)
    ? props.position_capacity.allocation
    : "not"
);

// Same rule as the trigger: Not Active positions and any occupied state are read-only.
const canChangeCapacity = computed(
  () => props.capacityEditable && isActiveState.value && triggerAllocation.value === "not"
);

const showDepartmentLine = computed(() => props.department !== null);

const validityBadgeProps = computed(() => ({
  showRoleResponsibilityCounts: showRoleResponsibilityCounts.value,
  isInvalid: props.position_capacity.isInvalid,
  directResponsibilitiesCount: props.position_capacity.directResponsibilitiesCount,
  notActiveDirectResponsibilitiesCount: props.position_capacity.notActiveDirectResponsibilitiesCount,
  validRolesCount: props.position_capacity.validRolesCount,
  invalidRolesCount: props.position_capacity.invalidRolesCount,
  validResponsibilitiesCount: props.position_capacity.validResponsibilitiesCount,
  status: isActiveState.value ? "active" : "draft",
  paintInvalid: paintInvalid.value,
  invalidityReason: props.invalidity_reason,
}));

const showRoleResponsibilityCounts = computed(() =>
  isPositionRolesAndResponsibilitiesUiEnabled()
);

// Backend may include "#" on `code`; we always show exactly one leading "#".
const displayPositionCode = computed(() => {
  const raw = String(props.position_code ?? "").trim();
  if (!raw) return "";
  return `#${raw.replace(/^#+/, "")}`;
});

const hasDepartment = computed(() => Boolean(props.department?.trim()));

const isDepartmentTruncated = computed(
  () => hasDepartment.value && props.department.length > 20
);

// Calculate if position is "new" (created within last 3 days)
const isNew = computed(() => {
  if (!props.created_on) return false;
  const createdDate = new Date(props.created_on);
  const today = new Date();
  const diffTime = Math.abs(today - createdDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= 3;
});

const onClickValidityBadge = () => {
  emit("clickValidityBadge");
};

const focusCapacityTrigger = () =>
  capacityWrap.value?.querySelector("button.position-capacity-trigger")?.focus();

const closeCapacityOnScroll = (event) => {
  if (!event.target?.closest?.(`.${CAPACITY_MENU_CLASS}`)) capacityOpen.value = false;
};

const onClickCapacityTrigger = () => {
  emit("capacityTriggerClick");
  if (!canChangeCapacity.value) return;
  if (!capacityOpen.value) {
    const rect = capacityWrap.value?.getBoundingClientRect();
    capacityOpensUp.value = Boolean(rect) && rect.bottom + CAPACITY_SELECTOR_HEIGHT > window.innerHeight;
  }
  capacityOpen.value = !capacityOpen.value;
};

const onSelectCapacity = (capacity) => {
  capacityOpen.value = false;
  emit("capacityChange", capacity);
};

watch(canChangeCapacity, (allowed) => {
  if (!allowed) capacityOpen.value = false;
});

watch(capacityOpen, async (isOpen, wasOpen) => {
  if (isOpen) {
    window.addEventListener("scroll", closeCapacityOnScroll, true);
    return;
  }
  window.removeEventListener("scroll", closeCapacityOnScroll, true);
  if (!wasOpen) return;
  await nextTick();
  // Escape and a pick leave focus inside the hidden menu; an outside click already moved it elsewhere.
  const active = document.activeElement;
  if (!active || active === document.body || active.closest?.(`.${CAPACITY_MENU_CLASS}`)) {
    focusCapacityTrigger();
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", closeCapacityOnScroll, true);
});

const handleCardClick = () => {
  emit("positionClick");
};
</script>

<style>
/* The menu content is detached to the app root, so this block cannot be scoped. */
.v-menu__content.position-card__capacity-menu {
  background: transparent;
  box-shadow: none;
  overflow: visible;
  contain: none;
}
</style>

<style scoped>
.ex-org-position-card {
  display: inline-block;
  flex-direction: column;
  padding: 0px 40px 4px 0px;
  transition: background-color 0.2s ease;
}

/* Hover state - light purple background ONLY when hovered prop is true AND mouse is hovering */
.ex-org-position-card.hovered:hover {
  background-color: #f4ebff;
  cursor: pointer;
  padding: 0px 40px 4px 0px;
}

/* Redesign (idea-MDP-636): trigger + code/name, counters, department stacked with a 3px gap. */
.ex-org-position-card.ex-org-position-card--redesign {
  display: inline-flex;
  flex-direction: column;
  gap: 3px;
  padding: 4px;
  border-radius: 4px;
}

.ex-org-position-card.ex-org-position-card--redesign.hovered:hover {
  background-color: #f2edff;
  padding: 4px;
}

/* Figma badge is 38x22 including its 1px border, so padding is reduced by the border width. */
.ex-org-position-card--redesign .new-badge {
  padding: 1px 5px;
}

.position-header-row {
  display: flex;
  align-items: center;
  gap: 5px;
}

.position-capacity-trigger-wrap {
  display: inline-flex;
}

.position-counters {
  align-self: flex-start;
}

.badges-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.new-badge {
  border-radius: 6px;
  border: 1px solid #e9d7fe;
  background: #f9f5ff;
  color: #6941c6;
  text-align: center;
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  padding: 2px 6px;
}

.position-info {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.position-code {
  color: #475467;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
}

.position-name {
  color: #101828;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
}

.position-department {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.position-department-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.position-department-icon ::v-deep svg {
  display: block;
  width: 16px;
  height: 16px;
}

.position-department-name {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
  color: #101828;
  font-family: Inter;
  font-size: 10px;
  font-weight: 500;
  line-height: 25px;
}

/* Inactive state - grayed out appearance */
.ex-org-position-card.inactive .position-code {
  color: #98a2b3;
}

.ex-org-position-card.inactive .position-name {
  color: #98a2b3;
}

.ex-org-position-card.inactive .position-department-name {
  color: #98a2b3;
}
</style>
